/** @jsxImportSource @emotion/react */
import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { ErrorsByTimeSummaryQuery } from "./__generated__/ErrorsByTimeSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const chartHeaderCSS = css`
  margin-bottom: 24px;
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const bigNumberCSS = css`
  font-size: 38px;
  font-weight: 900;
  color: var(--ac-global-text-color-900);
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -1.5px;
`;

const iconWrapperCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(244, 63, 94, 0.1);
  color: #f43f5e;
  font-size: 22px;
`;

const subtitleCSS = css`
  font-size: 14px;
  font-weight: 500;
  color: var(--ac-global-text-color-500);
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 0;
`;

const viewTracesLinkCSS = css`
  display: block;
  margin-top: 12px;
  font-size: 11px;
  color: var(--ac-global-color-primary);
  text-decoration: none;
  font-weight: 600;
  border-top: 1px solid var(--ac-global-color-grey-100);
  padding-top: 8px;
  &:hover {
    text-decoration: underline;
  }
`;

function CustomTooltip({
    active,
    payload,
    label,
    scale,
}: TooltipContentProps<number, string> & { scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        });

        return (
            <ChartTooltip>
                {label && (
                    <Text weight="heavy" size="S" color="secondary">{timeStr}</Text>
                )}
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {payload.map((item, idx) => (
                        <ChartTooltipItem
                            key={idx}
                            color={item.color}
                            shape={item.name?.includes("Rate") ? "line" : "circle"}
                            name={item.name || ""}
                            value={item.name?.includes("Rate") ? `${(item.value ?? 0).toFixed(1)}%` : intShortFormatter(item.value ?? 0)}
                        />
                    ))}
                </div>
                <a href="#" onClick={(e) => e.preventDefault()} css={viewTracesLinkCSS}>
                    View traces for this period
                </a>
            </ChartTooltip>
        );
    }
    return null;
}

export function ErrorsByTimeSummary({ projectId, timeRange, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    // Machi, respect the aggregation! Week/Month/Day (5-35 days) should stay as DAY for cleaner views
    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<ErrorsByTimeSummaryQuery>(
        graphql`
      query ErrorsByTimeSummaryQuery(
        $projectId: ID!
        $timeRange: TimeRange!
        $timeBinConfig: TimeBinConfig!
      ) {
        project: node(id: $projectId) {
          ... on Project {
            traceCountByStatusTimeSeries(
              timeRange: $timeRange
              timeBinConfig: $timeBinConfig
            ) {
              data {
                timestamp
                okCount
                errorCount
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
            timeBinConfig: {
                scale,
                utcOffsetMinutes,
            },
        }
    );

    const { chartData, totalErrors, overallRate } = useMemo(() => {
        const raw = data.project?.traceCountByStatusTimeSeries?.data ?? [];
        let errSum = 0;
        let totalCount = 0;

        const chart = raw.map((d) => {
            const sum = d.okCount + d.errorCount;
            const rate = sum > 0 ? (d.errorCount / sum) * 100 : 0;
            errSum += d.errorCount;
            totalCount += sum;
            return {
                timestamp: d.timestamp,
                errorCount: d.errorCount,
                errorRate: rate,
            };
        });

        const rate = totalCount > 0 ? (errSum / totalCount) * 100 : 0;

        return {
            chartData: chart,
            totalErrors: errSum,
            overallRate: rate
        };
    }, [data.project?.traceCountByStatusTimeSeries?.data]);

    useEffect(() => {
        if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, chartData);
        }
    }, [chartData, onDataExport, widgetId, widgetTitle]);

    const timeTickFormatter = (value: string) => {
        const date = new Date(value);
        if (scale === "DAY") {
            return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
        }
        return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', hour12: true });
    };

    return (
        <div css={containerCSS}>
            <div css={chartHeaderCSS}>
                <div css={bigNumberCSS}>
                    <div css={iconWrapperCSS}>⚠️</div>
                    {totalErrors}
                </div>
                <div css={subtitleCSS}>
                    (Overall error rate: {overallRate.toFixed(1)}%)
                </div>
            </div>

            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                        <defs>
                            <linearGradient id="colorErrorArea" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#be123c" stopOpacity={0.6} />
                                <stop offset="95%" stopColor="#be123c" stopOpacity={0.2} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.4} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            tickFormatter={timeTickFormatter}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            axisLine={false}
                        />
                        <YAxis
                            yAxisId="left"
                            {...defaultYAxisProps}
                            width={40}
                            tickFormatter={(x) => intShortFormatter(x)}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            axisLine={false}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            {...defaultYAxisProps}
                            width={40}
                            tickFormatter={(x) => `${x}%`}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            axisLine={false}
                        />
                        <Tooltip content={(props: any) => <CustomTooltip {...props} scale={scale} />} cursor={{ stroke: "#be123c", strokeWidth: 2, strokeDasharray: "4 4" }} />

                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="errorCount"
                            name="Error Count"
                            stroke="none"
                            fill="url(#colorErrorArea)"
                        />

                        <Bar
                            yAxisId="left"
                            dataKey="errorCount"
                            name="Error Count"
                            fill="#be123c"
                            barSize={8}
                            radius={[4, 4, 0, 0]}
                            opacity={0.8}
                        />

                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="errorRate"
                            name="Error Rate"
                            stroke="#b45309"
                            strokeWidth={3}
                            dot={{ r: 4, fill: "#eab308", stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 7, fill: "#fff", stroke: "#fff", strokeWidth: 4, strokeOpacity: 0.3 }}
                        />

                        <Legend
                            {...defaultLegendProps}
                            iconType="circle"
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{ paddingTop: '20px' }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
